import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Button, Card, Col, Row } from 'antd';

import axios from 'axios';
import Alert from 'components/Alert';
import { API_MYEDUZZ } from 'settings';
import RegistryBlockModal from 'v2/components/RegistryBlockModal';
import GlobalLoading from 'v2/components/Shared/GlobalLoading';
import NoDangerouslyHTML from 'v2/components/Shared/NoDangerouslyHTML';
import PublicLayout from 'v2/components/Shared/PublicLayout';
import Truncate from 'v2/components/Shared/TruncateText';
import { formatterApiErrors } from 'v2/formatters/apiErrors';
import { formatMoney, formatPercentual } from 'v2/helpers/money';
import useGetPermissions from 'v2/hooks/useGetPermissions';
import useGetUser from 'v2/hooks/useGetUser';
import { useRegistryCheck } from 'v2/hooks/useRegistryCheck';
import affiliateService from 'v2/services/affiliate';

interface IProduct {
  id: number;
  title: string;
  description: string;
  producer_name: string;
  showcase_commission: string;
  image: string;
  recruiter_name: string;
  recruiter_id: number;
  commission_type: string;
  commission_value: number;
}

const InviteAffiliate = () => {
  const [product, setProduct] = useState<IProduct>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { productId, affiliateId } = useParams<{ productId: string; affiliateId?: string }>();

  const user = useGetUser();

  useEffect(() => {
    if (productId) {
      let url = `${API_MYEDUZZ}/showcase/public/${productId}`;
      if (affiliateId) url = `${url}?recruiter=${affiliateId}`;
      axios
        .get(url)
        .then(res => {
          setProduct(res.data.data);
          setIsLoading(false);
        })
        .catch(e => {
          toast.error(formatterApiErrors(e), { autoClose: false });
        });
    }
  }, [affiliateId, productId]);

  const affiliateCommissionForSale = useMemo(() => {
    if (!product?.commission_type || !product?.commission_value) return '';
    const isPercentual = product.commission_type === 'P';

    const formmater = isPercentual ? formatPercentual : formatMoney;
    const commission = formmater(product.commission_value);

    return `até ${commission}`;
  }, [product]);

  if (isLoading) return <GlobalLoading />;

  return (
    <PublicLayout>
      <Row className='my-6'>
        <Col span={24}>
          <h4>
            Você foi convidado por {product.recruiter_name !== '' ? product.recruiter_name : product.producer_name} a se
            tornar afiliado do produto abaixo:
          </h4>
        </Col>
      </Row>
      <Card className='p-6'>
        <Row gutter={[16, 16]} justify='start' align='middle'>
          <Col xs={8}>
            <div className='max-h-30 max-w-30'>
              <img className='max-h-full max-w-full' src={product.image} alt='Preview' />
            </div>
          </Col>
          <Col xs={16}>
            <div className='leading-1.2 text-base font-semibold'>{product.title}</div>
            <div className='text-[0.9rem] text-(--uizz-antd-color-success)'>
              Você receberá <b>{affiliateCommissionForSale}</b> por venda
            </div>
          </Col>
          <Col span={24}>
            <div className='text-content-caption text-sm leading-normal font-normal tracking-[0.01071em] wrap-break-word'>
              <Truncate final='...' count={1090}>
                <NoDangerouslyHTML html={product.description} />
              </Truncate>
            </div>
          </Col>
          {user ? <Authenticated product={product} /> : <NoAuthenticated />}
        </Row>
      </Card>
      <Row className='my-6'>
        <Col span={24}>
          <h4>
            Você receberá um link e poderá ganhar dinheiro vendendo este produto em sites, blogs, redes sociais e
            e-mail.{' '}
            <a
              target='_blank'
              rel='noopener noreferrer'
              href='https://ajuda.eduzz.com/?article=primeiro-passos-de-um-afiliado'
            >
              Saiba Mais
            </a>
          </h4>
        </Col>
      </Row>
    </PublicLayout>
  );
};

const NoAuthenticated = () => {
  const { productId, affiliateId } = useParams<{ productId: string; affiliateId?: string }>();
  let url = `/affiliate/invite/${productId}`;
  if (affiliateId) url = `${url}/${affiliateId}`;
  return (
    <div className='mt-4 flex w-full flex-col gap-2'>
      <Link to={`${url}?login=true&register=true`}>
        <Button type='primary' block>
          Fazer meu cadastro
        </Button>
      </Link>
      <Link to={`${url}?login=true`}>
        <Button id='btn-already-have-account' block>
          Já tenho cadastro
        </Button>
      </Link>
    </div>
  );
};

const Authenticated = ({ product }: { product: IProduct }) => {
  const user = useGetUser();
  const permissions = useGetPermissions();
  const { data: registryCheck, isLoading: isLoadingRegistryCheck, shouldBlock, dismissBlock } = useRegistryCheck();

  const [isSending, setIsSending] = useState<boolean>(false);
  const [done, setDone] = useState<boolean>(false);
  const [showRegistryBlockModal, setShowRegistryBlockModal] = useState(false);

  const openModal = useCallback(async () => {
    const confirm = await Alert.confirm({
      message: <div className='text-base'>Você deseja realmente solicitar afiliação desse produto?</div>,
      title: <>Solicitar afiliação</>,
      buttonOk: <>AFILIAR-SE</>,
      buttonCancel: <>Fechar</>,
      confirmation: true
    });

    if (confirm) {
      const fetchData = async () => {
        try {
          affiliateService.inviteAffiliate(product.id, product.recruiter_id, user.id).subscribe(
            () => {
              setDone(true);
              setIsSending(false);
              toast.success(
                'Afiliação solicitada com sucesso, agora você pode acompanhar o status da sua solicitação em minhas afiliações'
              );
            },
            err => {
              toast.error(err);
              setIsSending(false);
            }
          );
        } catch (error) {
          toast.error(error.message);
          setIsSending(false);
        }
      };

      return fetchData();
    }

    setIsSending(false);
  }, [product.id, product.recruiter_id, user?.id]);

  const handleClick = useCallback(() => {
    setIsSending(true);

    if (shouldBlock) {
      setShowRegistryBlockModal(true);
      return;
    }

    void openModal();
  }, [shouldBlock, openModal]);

  const handleCancelRegistryBlockModal = useCallback(() => {
    dismissBlock();
    setShowRegistryBlockModal(false);
    void openModal();
  }, [openModal, dismissBlock]);

  if (done) {
    return (
      <div className='mt-4 flex w-full justify-end'>
        <Link to='/producer/affiliations'>
          <Button id='btn-go-to-affiliations' type='primary'>
            Ir para minhas afiliações
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <>
      <div className='mt-4 flex w-full justify-end'>
        <Button
          id='btn-become-affiliate'
          type='primary'
          loading={isSending}
          onClick={handleClick}
          disabled={isSending || isLoadingRegistryCheck}
        >
          Afiliar-se
        </Button>
      </div>

      <RegistryBlockModal
        opened={showRegistryBlockModal}
        isOwner={permissions?.owned}
        cancelable={registryCheck?.skip}
        onCancel={handleCancelRegistryBlockModal}
      />
    </>
  );
};

export default InviteAffiliate;
